<template>
    <modal-dialog
        ref="modal"
        label="Vraag je nieuwe functionaliteit op maat aan"
        description="Vraag je nieuwe functionaliteit op maat aan"
        without-padding
        hide-close
    >
        <form-validation v-slot="{ bindings: formValidationBindings }">
            <flex-container
                :gap="Size.sm"
            >
                <flex-container
                    :gap="Size.xs"
                    :padding="Size.md"
                    direction="column"
                >
                    <text-heading>
                        <slot name="heading">
                            Wat wil je bereiken?
                        </slot>
                    </text-heading>
                    <text-description>
                        <slot name="description">
                            Beschrijf hieronder zo duidelijk mogelijk wat je wilt bereiken. Klik op ‘Versturen’ als je klaar bent. Ons Customer Success-team leest je bericht en laat je weten hoe we je kunnen helpen.
                        </slot>
                    </text-description>

                    <flex-container :padding-y="Size.md">
                        <input-group
                            v-model="newFunctionalityDescription"
                            :schema="string().required()"
                        >
                            <template #input>
                                <slot name="input">
                                    <text-area-input
                                        height="200px"
                                        placeholder="Beschrijf het doel en welke informatie verzameld moet worden."
                                    />
                                </slot>
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container align-items="center" justify-content="flex-end">
                        <button-text @click="modal?.closeOverlay()">
                            Annuleren
                        </button-text>
                        <button-block :disabled="!formValidationBindings.isValid.value" @loading="requestNewFunctionality($event)">
                            Versturen
                        </button-block>
                    </flex-container>
                </flex-container>
            </flex-container>
        </form-validation>
    </modal-dialog>
</template>

<script setup lang="ts">
    import {
        ButtonState,
        type ButtonStateChangedEvent,
        type ExposedOverlay,
        FlexContainer,
        getCssColor,
        InputGroup,
        NotificationBlock,
        Size,
        TextAreaInput,
        TextDescription,
        TextHeading,
        useNotify,
    } from '@keyvue/one-ui';
    import { string } from 'yup';
    import { ref } from 'vue';
    import { RequestNewFunctionalityCommand, Assistent } from '@keyvue/one-api-client';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useAppBranding from '@/services/app-branding/composables/useAppBranding';
    import { useI18n } from 'vue-i18n';
    import messages from './RequestNewFunctionalityModal.messages.json';

    const modal = ref<ExposedOverlay>();
    const newFunctionalityDescription = ref<string>();
    const { client, currentUser, currentCompany } = useClient();
    const { hasWinckBranding } = useAppBranding();
    const { notify } = useNotify();
    const { t } = useI18n({ messages });

    interface Props {
        assistent: Assistent;
        functionality: string;
    }

    const props = defineProps<Props>();

    async function requestNewFunctionality(event: ButtonStateChangedEvent): Promise<void> {
        const userEmail = currentUser.value.email;
        const { name: assistantName, id: assistantId } = props.assistent;

        const subject = t('emailSubject', {
            functionality: props.functionality,
            userEmail,
        });

        const emailUser = t('emailContent', {
            userName: currentUser.value.name,
            userEmail,
            companyName: currentCompany.value.name,
            functionality: props.functionality,
            assistantName,
            assistantId,
        });

        const body = [
            emailUser,
            t('emailDescription'),
            newFunctionalityDescription.value,
        ].join('\n');

        await client.dispatch(
            new RequestNewFunctionalityCommand(
                'no-reply-new-functionality@veiligverstuurd.nl',
                currentUser.value.name.toString(),
                hasWinckBranding() ? 'support@winck.nl' : 'welcome@asknoa.ai',
                subject,
                body,
            ),
        );

        event.button.setState(ButtonState.completed);
        notify({
            component: NotificationBlock,
            props: {
                color: getCssColor('default', 'green-300'),
                title: 'Aanvraag succesvol verstuurd!',
                text: `Je ${props.functionality} beschrijving is succesvol verstuurd naar ons Customer Success-team`,
            },
            duration: 3000,
        });
        newFunctionalityDescription.value = '';
        modal.value?.closeOverlay();
    }

    defineExpose({
        openOverlay: () => {
            modal.value?.openOverlay();
        },
        closeOverlay: () => {
            modal.value?.closeOverlay();
        },
    });
</script>
